import { Rng } from '../rng';

/**
 * Gig bags (plan doc 13) — `chest` in the code and the database, because the
 * rows and the RPC predate the name.
 *
 * A gig bag is a reward for a finished unit, a checkpoint or every fifth
 * level. It is never sold, and it holds Picks or a cosmetic — never anything
 * consumable, so there is nothing in it that changes how anyone learns.
 *
 * The roll is seeded and pure so the server can do it authoritatively and a
 * test can prove the distribution. The client never rolls a real bag — it
 * plays the animation for whatever the server returns — but the same function
 * drives the preview of what a bag *can* contain, so the odds shown are the
 * odds used.
 */

export type ChestKind = 'bronze' | 'silver' | 'gold';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type ChestReward = { kind: 'gems'; amount: number } | { kind: 'cosmetic'; rarity: Rarity };

interface Slice {
  /** Relative weight, out of a hundred. */
  weight: number;
  roll: (rng: Rng) => ChestReward;
}

const gems = (low: number, high: number): Slice['roll'] => {
  return (rng) => ({ kind: 'gems', amount: rng.int(low, high) });
};

/** The tables, as weights out of a hundred. Mirrored by `open_chest` in SQL. */
export const CHEST_TABLES: Readonly<Record<ChestKind, readonly Slice[]>> = {
  bronze: [
    { weight: 70, roll: gems(30, 60) },
    { weight: 30, roll: () => ({ kind: 'cosmetic', rarity: 'common' }) },
  ],
  silver: [
    { weight: 50, roll: gems(80, 150) },
    {
      weight: 50,
      roll: (rng) => ({ kind: 'cosmetic', rarity: rng.chance(0.6) ? 'uncommon' : 'rare' }),
    },
  ],
  gold: [
    { weight: 40, roll: gems(200, 400) },
    {
      weight: 60,
      roll: (rng) => ({ kind: 'cosmetic', rarity: rng.chance(0.6) ? 'rare' : 'epic' }),
    },
  ],
};

/**
 * Opens a chest.
 *
 * A cosmetic the learner already has would be a disappointment dressed as a
 * prize, so `owned` is consulted: when nothing of that rarity is left, the
 * cosmetic converts to gems at its shop price, which is what the plan asks for.
 */
export function openChest(
  kind: ChestKind,
  seed: number,
  options: { availableByRarity?: Partial<Record<Rarity, number>> } = {},
): ChestReward {
  const rng = new Rng(seed);
  const table = CHEST_TABLES[kind];
  const total = table.reduce((sum, slice) => sum + slice.weight, 0);

  let roll = rng.float() * total;
  for (const slice of table) {
    roll -= slice.weight;
    if (roll > 0) continue;
    const reward = slice.roll(rng);
    if (reward.kind !== 'cosmetic') return reward;
    const available = options.availableByRarity?.[reward.rarity] ?? 1;
    if (available > 0) return reward;
    return { kind: 'gems', amount: COSMETIC_GEM_VALUE[reward.rarity] };
  }
  // Unreachable while the weights sum above zero; a gem drop is the safe floor.
  return { kind: 'gems', amount: 30 };
}

/** What a duplicate cosmetic converts to. */
export const COSMETIC_GEM_VALUE: Readonly<Record<Rarity, number>> = {
  common: 150,
  uncommon: 350,
  rare: 700,
  epic: 1200,
  legendary: 0,
};

/** The odds, as percentages, for the "what's inside" list on a chest card. */
export function chestOdds(kind: ChestKind): { label: string; percent: number }[] {
  const table = CHEST_TABLES[kind];
  const total = table.reduce((sum, slice) => sum + slice.weight, 0);
  const rng = new Rng(1);
  return table.map((slice) => {
    const sample = slice.roll(rng);
    const label = sample.kind === 'gems' ? 'Picks' : 'A cosmetic';
    return { label, percent: Math.round((slice.weight / total) * 100) };
  });
}

/** What earns a gig bag. */
export type ChestSource = 'unit' | 'checkpoint' | 'level';

export function chestFor(source: ChestSource): ChestKind {
  return source === 'checkpoint' ? 'silver' : 'bronze';
}

/** Every fifth level pays a chest. */
export function levelChest(levelBefore: number, levelAfter: number): number {
  return Math.floor(levelAfter / 5) - Math.floor(levelBefore / 5);
}
